import { InferOutput, object, safeParse, string, ValiError } from 'valibot';
import { FetchError } from '../utils.ts';

const API_DOMAIN = import.meta.env.VITE_LICENSE_API_URL;

/**
 * Schema for the parameters of the `/v1/auth/signing` endpoint.
 */
export const AuthSigningRequestArgsSchema = object({
  ecdh_public_key: string(),
});

/**
 * Type for the parameters of the `/v1/auth/signing` endpoint.
 */
export type AuthSigningRequestArgs = InferOutput<
  typeof AuthSigningRequestArgsSchema
>;

/**
 * Schema for the response of the `/v1/auth/signing` endpoint.
 */
export const AuthSigningResponseSchema = object({
  message: string(),
  ecdh_public_key: string(),
  salt: string(),
  iv: string(),
  key: string(),
  authTag: string(),
});

/**
 * Type for the response of the `/v1/auth/signing` endpoint.
 */
export type AuthSigningResponse = InferOutput<
  typeof AuthSigningResponseSchema
>;

/**
 * Request the signing key from the license server.
 * @param {AuthSigningRequestArgs} param The parameters for the request.
 * @returns {Promise<AuthSigningResponse>} The signing key response.
 */
export const $requestAuthSigning = async (
  param: AuthSigningRequestArgs,
): Promise<AuthSigningResponse> => {
  const p = safeParse(AuthSigningRequestArgsSchema, param);
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const { ecdh_public_key } = p.output;

  const res = await fetch(`${API_DOMAIN}/v1/auth/signing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ecdh_public_key,
    }),
  });

  if (!res.ok) {
    const message = `Error retrieving signing key (${res.status})`;
    throw new FetchError(message, res.status);
  }
  const data = await res.json();

  const r = safeParse(AuthSigningResponseSchema, data);
  if (!r.success) {
    throw new ValiError(r.issues);
  }

  return r.output;
};
